import React from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { css, StyleSheet } from 'aphrodite';
import compose from 'recompose/compose';
import withProps from 'recompose/withProps';
import withState from 'recompose/withState';
import withHandlers from 'recompose/withHandlers';
import Icon from 'react-svg-ikon';

import { TSS_BLUE_1, TSS_BLUE_2, TSS_GREEN_1, momentPropType, stylePropType, dateType, range } from 'tss-core-lib-common';

import { IconButton } from './buttons';
import TimePicker from './TimePicker';
import { IMG_ICON_CHEVRON_LEFT, IMG_ICON_DOUBLE_CHEVRON_LEFT, IMG_ICON_CHEVRON_RIGHT, IMG_ICON_DOUBLE_CHEVRON_RIGHT } from '../img/icons';

var styles = void 0;

var weekDayOffsets = [0, 1, 2, 3, 4, 5, 6];

var Week = function Week(_ref) {
  var startDate = _ref.startDate,
      month = _ref.month,
      today = _ref.today,
      value = _ref.value,
      min = _ref.min,
      max = _ref.max,
      onDateClick = _ref.onDateClick;
  return React.createElement(
    'div',
    { className: css(styles.weekContainer) },
    weekDayOffsets.map(function (offset) {
      var date = startDate.clone().add(offset, 'days').startOf('day');
      var isRestricted = min && date.clone().endOf('day').isBefore(min) || max && date.clone().startOf('day').isAfter(max);
      var className = css(styles.weekday, offset === 0 && styles.weekdayFirstDayOfWeek, !date.isSame(month, 'month') && styles.weekdayOutsideMonth, today && today.isSame(date, 'day') && styles.weekdayToday, value && value.isSame(date, 'day') && styles.weekdaySelected, isRestricted && styles.weekdayRestricted);

      return React.createElement(
        'div',
        {
          key: date.unix(),
          className: className,
          'data-date': date.format('YYYY-MM-DD'),
          onClick: !isRestricted ? onDateClick : null
        },
        date.date()
      );
    })
  );
};

Week.propTypes = {
  startDate: momentPropType.isRequired,
  month: momentPropType.isRequired,
  today: momentPropType,
  value: momentPropType,
  min: momentPropType,
  max: momentPropType,
  onDateClick: PropTypes.func.isRequired
};

Week.defaultProps = {
  today: null,
  value: null,
  min: null,
  max: null
};

var isMomentUsing24H = function isMomentUsing24H() {
  return Boolean(moment().format('LT').match(/[AP]M$/));
};

var MonthSelector = function MonthSelector(_ref2) {
  var month = _ref2.month,
      onMonthClick = _ref2.onMonthClick;
  return React.createElement(
    'div',
    { className: css(styles.monthSelectorContainer) },
    range(0, 12).map(function (monthNr) {
      return React.createElement(
        'button',
        {
          key: monthNr,
          className: css(styles.monthButton, monthNr === month.month() && styles.currentMonthButton),
          onClick: function onClick() {
            return onMonthClick(month.clone().month(monthNr));
          }
        },
        month.clone().month(monthNr).format('MMMM YYYY')
      );
    })
  );
};

MonthSelector.propTypes = {
  month: momentPropType.isRequired,
  onMonthClick: PropTypes.func.isRequired
};

var enhanceDatePicker = compose(withState('monthSelectorOpen', 'setMonthSelectorOpen', false), withProps(function (props) {
  var mMin = props.min ? moment.utc(props.min).tz(props.timezone) : null;
  var mMax = props.max ? moment.utc(props.max).tz(props.timezone) : null;

  var newProps = {
    mMin: mMin,
    mMax: mMax
  };

  var momentValue = typeof props.value === 'number' ? moment.utc(props.value) : props.value;

  if (!props.month) {
    if (momentValue) {
      return Object.assign({}, newProps, { month: momentValue.clone() });
    }
    var month = moment();
    if (mMin && mMin.isAfter(month)) {
      month = mMin.clone().startOf('month');
    } else if (mMax && !mMax.isAfter(month)) {
      month = mMax.clone().startOf('month');
    }
    if (props.locale) {
      month = month.locale(props.locale);
    }
    if (props.timezone) {
      month = month.tz(props.timezone);
    }
    return Object.assign({}, newProps, { month: month });
  }
  return newProps;
}), withState('mCurrentMonth', 'setCurrentMonth', function (props) {
  return props.month.clone();
}), withHandlers({
  onDateClick: function onDateClick(props) {
    return function (event) {
      if (props.monthSelectorOpen) {
        props.setMonthSelectorOpen(false);
        return;
      }

      var dataDate = event.target.getAttribute('data-date');
      if (dataDate && props.onChange) {
        var newDate = void 0;
        if (props.timezone) {
          newDate = moment.tz(dataDate, props.timezone);
        } else {
          newDate = moment.utc(dataDate);
        }

        if (props.value) {
          newDate.minutes(props.value.minutes()).hours(props.value.hours());
        }

        if (props.mMin) {
          newDate = moment.max(props.mMin, newDate);
        }

        if (props.mMax) {
          newDate = moment.min(props.mMax, newDate);
        }

        props.onChange(props.timezone ? newDate.utc().tz(props.timezone) : newDate.utc());
      }
    };
  },

  onDateChange: function onDateChange(props) {
    return function (date) {
      props.onChange(date);
      props.setMonthSelectorOpen(false);
    };
  },

  onTimeChange: function onTimeChange(props) {
    return function (date) {
      props.onTimeChange(date);
      props.setMonthSelectorOpen(false);
    };
  },

  onPrevMonthClick: function onPrevMonthClick(props) {
    return function (event) {
      event.preventDefault();
      props.setCurrentMonth(props.mCurrentMonth.clone().subtract(1, 'month'));
    };
  },

  onNextMonthClick: function onNextMonthClick(props) {
    return function (event) {
      event.preventDefault();
      props.setCurrentMonth(props.mCurrentMonth.clone().add(1, 'month'));
    };
  },

  onPrevYearClick: function onPrevYearClick(props) {
    return function (event) {
      event.preventDefault();
      props.setCurrentMonth(props.mCurrentMonth.clone().subtract(1, 'year'));
    };
  },

  onNextYearClick: function onNextYearClick(props) {
    return function (event) {
      event.preventDefault();
      props.setCurrentMonth(props.mCurrentMonth.clone().add(1, 'year'));
    };
  },

  onMonthClick: function onMonthClick(props) {
    return function (month) {
      event.preventDefault();
      props.setCurrentMonth(props.mCurrentMonth.clone().month(month.month()));
      setTimeout(function () {
        return props.setMonthSelectorOpen(false);
      }, 0);
    };
  },

  onOpenMonthSelector: function onOpenMonthSelector(props) {
    return function () {
      return props.setMonthSelectorOpen(true);
    };
  }
}));

var DatePicker = function DatePicker(_ref3) {
  var mCurrentMonth = _ref3.mCurrentMonth,
      today = _ref3.today,
      value = _ref3.value,
      mMin = _ref3.mMin,
      mMax = _ref3.mMax,
      h24 = _ref3.h24,
      showTimePicker = _ref3.showTimePicker,
      timezone = _ref3.timezone,
      timezoneString = _ref3.timezoneString,
      allowMonthChange = _ref3.allowMonthChange,
      monthSelectorOpen = _ref3.monthSelectorOpen,
      style = _ref3.style,
      onPrevMonthClick = _ref3.onPrevMonthClick,
      onNextMonthClick = _ref3.onNextMonthClick,
      onPrevYearClick = _ref3.onPrevYearClick,
      onNextYearClick = _ref3.onNextYearClick,
      onDateClick = _ref3.onDateClick,
      onTimeChange = _ref3.onTimeChange,
      onMonthClick = _ref3.onMonthClick,
      onOpenMonthSelector = _ref3.onOpenMonthSelector;

  var startDate = mCurrentMonth.clone().startOf('month').startOf('week');
  var weeks = [];
  var mToday = null;
  if (today === true) {
    mToday = moment().startOf('day');
  } else if (today) {
    mToday = moment(today);
  }

  var momentValue = typeof value === 'number' ? moment.utc(value) : value;
  var mSelectedDate = momentValue || null;

  var finalH24 = h24 === null ? isMomentUsing24H() : h24;

  var prevMonthButtonDisabled = mMin && mMin.isAfter(mCurrentMonth.clone().subtract(1, 'month').endOf('month'));

  var nextMonthButtonDisabled = mMax && mMax.isBefore(mCurrentMonth.clone().add(1, 'month').startOf('month'));

  do {
    weeks.push(startDate.clone());
    startDate.add(7, 'days');
  } while (startDate.isSame(mCurrentMonth, 'month'));

  return React.createElement(
    'div',
    { className: css(styles.container) },
    React.createElement(
      'div',
      { className: css(styles.header, style && style.header) },
      allowMonthChange ? [React.createElement(
        IconButton,
        {
          key: 'prevyear',
          onClick: onPrevYearClick,
          disabled: prevMonthButtonDisabled,
          containerStyle: styles.arrowButton
        },
        React.createElement(Icon, { stroke: 'black', src: IMG_ICON_DOUBLE_CHEVRON_LEFT })
      ), React.createElement(
        IconButton,
        {
          key: 'prevmonth',
          onClick: onPrevMonthClick,
          disabled: prevMonthButtonDisabled,
          containerStyle: styles.arrowButton
        },
        React.createElement(Icon, { stroke: 'black', src: IMG_ICON_CHEVRON_LEFT })
      )] : null,
      React.createElement(
        'div',
        { className: css(styles.currentMonthNameContainer), role: 'button', tabIndex: 0, onClick: onOpenMonthSelector },
        React.createElement(
          'div',
          { className: css(styles.currentMonthNameButton) },
          mCurrentMonth.format('MMMM YYYY')
        ),
        monthSelectorOpen ? React.createElement(MonthSelector, { month: mCurrentMonth, onMonthClick: onMonthClick }) : null
      ),
      allowMonthChange ? [React.createElement(
        IconButton,
        {
          key: 'nextmonth',
          onClick: onNextMonthClick,
          disabled: nextMonthButtonDisabled,
          containerStyle: styles.arrowButton
        },
        React.createElement(Icon, { stroke: 'black', src: IMG_ICON_CHEVRON_RIGHT })
      ), React.createElement(
        IconButton,
        {
          key: 'nextyear',
          onClick: onNextYearClick,
          disabled: nextMonthButtonDisabled,
          containerStyle: styles.arrowButton
        },
        React.createElement(Icon, { stroke: 'black', src: IMG_ICON_DOUBLE_CHEVRON_RIGHT })
      )] : null
    ),
    React.createElement(
      'div',
      { className: css(styles.weekDayHeader) },
      weekDayOffsets.map(function (offset) {
        var date = weeks[0].clone().add(offset, 'days');
        return React.createElement(
          'div',
          { key: date.unix(), className: css(styles.weekDayAbbr) },
          date.format('dd')
        );
      })
    ),
    React.createElement(
      'div',
      { className: css(styles.weeksContainer) },
      weeks.map(function (weekStartDate) {
        return React.createElement(Week, {
          key: weekStartDate.unix(),
          startDate: weekStartDate,
          month: mCurrentMonth,
          today: mToday,
          value: mSelectedDate,
          min: mMin,
          max: mMax,
          onDateClick: onDateClick
        });
      })
    ),
    showTimePicker ? React.createElement(
      'div',
      { className: css(styles.timePickerContainer) },
      React.createElement(TimePicker, {
        min: mMin,
        max: mMax,
        h24: finalH24,
        timezone: timezone,
        timezoneString: timezoneString,
        selectedDate: mSelectedDate,
        onChange: onTimeChange
      })
    ) : null
  );
};

DatePicker.propTypes = {
  month: dateType.isRequired, // eslint-disable-line
  mCurrentMonth: dateType.isRequired,
  today: PropTypes.oneOfType([momentPropType, PropTypes.number, PropTypes.bool]),
  value: dateType,
  min: dateType, // eslint-disable-line
  mMin: momentPropType,
  max: dateType, // eslint-disable-line
  mMax: momentPropType,
  h24: PropTypes.bool,
  monthSelectorOpen: PropTypes.bool.isRequired,
  showTimePicker: PropTypes.bool,
  timezone: PropTypes.string,
  timezoneString: PropTypes.string,
  allowMonthChange: PropTypes.bool,
  style: PropTypes.shape({
    header: stylePropType,
    weekday: stylePropType
  }),
  onOpenMonthSelector: PropTypes.func.isRequired,
  onNextMonthClick: PropTypes.func.isRequired,
  onPrevMonthClick: PropTypes.func.isRequired,
  onPrevYearClick: PropTypes.func.isRequired,
  onNextYearClick: PropTypes.func.isRequired,
  onDateClick: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired, // eslint-disable-line
  onTimeChange: PropTypes.func.isRequired,
  onMonthClick: PropTypes.func.isRequired
};

DatePicker.defaultProps = {
  month: null,
  today: null,
  value: null,
  min: null,
  mMin: null,
  mMax: null,
  max: null,
  h24: null,
  showTimePicker: false,
  locale: null,
  timezone: null,
  timezoneString: null,
  allowMonthChange: true,
  style: null
};

var outerBorderColor = TSS_BLUE_1();
var innerBorderColor = TSS_BLUE_1();

styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: outerBorderColor,
    backgroundColor: 'white',
    maxWidth: '30rem',
    boxShadow: '0 2px 4px 0 rgba(0,0,0,0.3)'
  },

  weeksContainer: {},

  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '1rem 0.7rem'
  },

  arrowButton: {
    margin: '0 0.4rem'
  },

  currentMonthNameContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
    outline: 0
  },

  currentMonthNameButton: {
    textAlign: 'center',
    fontSize: '1.4rem',
    fontWeight: 500,
    padding: '0.6rem 1.2rem',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: TSS_BLUE_1(0.5)
    },
    outline: 0
  },

  monthSelectorContainer: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    width: '40rem',
    height: '20rem',
    marginTop: '-10rem',
    marginLeft: '-20rem',
    zIndex: 2,
    outline: 0,
    borderWidth: '0 1px',
    border: '1px solid ' + innerBorderColor,
    boxShadow: '0 2px 4px 0 rgba(0,0,0,0.3)',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    backgroundColor: 'white'
  },

  monthButton: {
    border: 0,
    padding: '1rem 0 ',
    textAlign: 'center',
    fontSize: '1.4rem',
    fontWeight: 400,
    width: '30%',
    outline: 0,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: TSS_BLUE_1(0.5)
    }
  },

  currentMonthButton: {
    fontWeight: 500
  },

  year: {
    display: 'inline-block',
    margin: '0 0 0 0.6rem'
  },

  weekContainer: {
    display: 'flex',
    alignItems: 'center',
    borderTop: '1px solid ' + innerBorderColor
  },

  weekDayHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.8rem 0'
  },

  weekDayAbbr: {
    fontSize: '1.2rem',
    fontWeight: 500,
    flex: 1,
    textAlign: 'center'
  },

  timePickerContainer: {
    padding: '1rem 0',
    borderTop: '1px solid ' + innerBorderColor
  },

  weekday: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.8rem 0',
    backgroundColor: 'white',
    color: 'black',
    fontSize: '1.4rem',
    fontWeight: 500,
    cursor: 'pointer',
    borderLeft: '1px solid ' + innerBorderColor,
    ':hover': {
      backgroundColor: '#f1f1f1'
    }
  },

  weekdayFirstDayOfWeek: {
    borderLeft: 0
  },

  weekdayToday: {
    backgroundColor: TSS_GREEN_1()
  },

  weekdaySelected: {
    backgroundColor: TSS_BLUE_2()
  },

  weekdayRestricted: {
    backgroundColor: '#c5c5c5',
    color: '#999',
    cursor: 'not-allowed'
  },

  weekdayOutsideMonth: {
    backgroundColor: '#f5f5f5'
  }
});

export default enhanceDatePicker(DatePicker);